import { ContentState, DraftHandleValue, EditorState, getDefaultKeyBinding, Modifier } from "draft-js";
import createEmojiPlugin, {
    type EmojiPlugin,
    type EmojiPluginConfig
} from "@draft-js-plugins/emoji";
import createMentionPlugin, {
    type MentionData,
    type MentionPluginConfig,
} from "@draft-js-plugins/mention";
import Editor from "@draft-js-plugins/editor"
import { Component, createRef, ReactNode, RefObject } from "react";
import { Subject, Subscription } from "rxjs";
import { debounceTime } from "rxjs/operators";
import { localize } from "src/l10n";
import { ISearchParams, searchUsers } from "src/search";
import { searchGroupMembers } from "src/groups/groups-api";
import startDraftWcagCheck from "src/utils/draftWcagCheck";
import { getContentStateFromString } from "src/utils/getContentStateFromString";
import { draftToHtml } from "./Composer/utils";

interface IInteractiveTextInputProps {
    onFocus?: Spintr.CallbackAction;
    onEnter?: Spintr.CallbackAction;
    onChange?: Spintr.CallbackAction;
    onBlur?: Spintr.CallbackAction;
    placeholder?: string;
    initialContent?: string;
    alternatePopoutDirection?: boolean;
    readOnly?: boolean;
    maxLength?: number;
    mentionSearchParams?: Partial<ISearchParams>;
}

interface IInteractiveTextInputState {
    editorState: EditorState;
    suggestions: MentionData[];
    mentionOpen: boolean;
}

class InteractiveTextInput extends Component<IInteractiveTextInputProps, IInteractiveTextInputState> {
    protected mentionPlugin: ReturnType<typeof createMentionPlugin>;
    protected emojiPlugin: EmojiPlugin;
    protected editorRef: RefObject<Editor>;
    protected mentionSubject: Subject<string>;
    protected mentionSubscription: Subscription;

    constructor(props: IInteractiveTextInputProps) {
        super(props);

        this.state = {
            editorState: props.initialContent ?
                EditorState.createWithContent(getContentStateFromString(props.initialContent)) :
                EditorState.createEmpty(),
            suggestions: [],
            mentionOpen: false,
        }

        this.onAddMention = this.onAddMention.bind(this);
        this.onChange = this.onChange.bind(this);
        this.onSearchChange = this.onSearchChange.bind(this);

        if (this.props.alternatePopoutDirection) {
            const mentionPluginOptions: MentionPluginConfig = {
                supportWhitespace: true,
                positionSuggestions: (settings) => {
                    let transform;
                    let transition;

                    if (settings.props.open && settings.props.suggestions.length > 0) {
                        transform = 'scaleY(1)';
                        transition = 'all 0.25s cubic-bezier(.3,1.2,.2,1)';
                    } else if (settings.props.open) {
                        transform = 'scaleY(0)';
                        transition = 'all 0.25s cubic-bezier(.3,1,.2,1)';
                    }

                    return {
                        transform,
                        transition,
                        bottom: "50px",
                        left: '0px',
                    };
                }
            };

            const emojiPluginOptions: EmojiPluginConfig = {
                positionSuggestions: () => {
                    return {
                        transform: 'scaleY(1)',
                        transition: 'all 0.25s cubic-bezier(.3,1.2,.2,1)',
                        bottom: "50px",
                        left: '0px',
                    };
                }
            };

            this.mentionPlugin = createMentionPlugin(mentionPluginOptions);
            this.emojiPlugin = createEmojiPlugin(emojiPluginOptions);
        } else {
            this.mentionPlugin = createMentionPlugin({
                supportWhitespace: true 
            });
            this.emojiPlugin = createEmojiPlugin();
        }

        this.editorRef = createRef();

        this.handleBeforeInput = this.handleBeforeInput.bind(this);
        this.onMentionValue = this.onMentionValue.bind(this);
        this.mentionSubject = new Subject();
        this.mentionSubscription = this.mentionSubject
            .pipe(
                debounceTime(100)
            )
            .subscribe(this.onMentionValue);

        startDraftWcagCheck();
    }

    protected async onMentionValue(value: string): Promise<void> {
        try {
            const query = value.trim();
            
            if (!query) {
                this.setState({ suggestions: [] });

                return;
            }

            let suggestions: MentionData[] = [];

            if (
                this.props.mentionSearchParams?.onlyGroupResults &&
                this.props.mentionSearchParams?.groupId !== undefined &&
                this.props.mentionSearchParams?.groupId !== null
            ) {
                const members = await searchGroupMembers({
                    groupId: this.props.mentionSearchParams.groupId,
                    searchText: query,
                    take: this.props.mentionSearchParams?.take,
                    skip: this.props.mentionSearchParams?.skip,
                });

                suggestions = members.map(member => ({
                    avatar: member.image,
                    key: member.id,
                    link: `/goto/1/${member.id}`,
                    name: member.name,
                }));
            } else {
                const params: ISearchParams = {
                    ...this.props.mentionSearchParams,
                    query,
                };

                const results = await searchUsers(params);

                suggestions = results.data.map(hit => ({
                    avatar: hit.imageUrl,
                    key: hit.id,
                    link: `/goto/1/${hit.id}`,
                    name: hit.name
                }));
            }

            this.setState({ suggestions });
        } catch (err) {
            console.log(err);
        }
    }

    public clearContent(): void {
        let editorState = EditorState.push(this.state.editorState, ContentState.createFromText(''), 'remove-range');
        const contentState = editorState.getCurrentContent()
        editorState = EditorState.forceSelection(editorState, contentState.getSelectionAfter())

        this.setState({
            editorState
        });
    }

    public getContent(): string {
        let content = draftToHtml(
            this.state.editorState.getCurrentContent()
        );

        if (content === "<br />") {
            content = "";
        }

        return content;
    }

    public giveFocus(): void {
        if (this.editorRef && this.editorRef.current) {
            requestAnimationFrame(() => {
                this.editorRef.current?.focus();
            });
        }
    }

    public myKeyBindingFn(e): string {
        if (!!this.props.onEnter &&
            e.keyCode === 13 && /* `Enter` key */
            !e.nativeEvent.shiftKey) {
            return 'custom-enter';
        }

        return getDefaultKeyBinding(e);
    }

    public handleKeyCommand(command) {
        if (command === 'custom-enter') {
            this.props.onEnter();

            return 'handled';
        }

        return 'not-handled';
    }

    handlePaste = (text): DraftHandleValue => {
        const { maxLength } = this.props;

        const currentContent = this.state.editorState.getCurrentContent();
        const currentLength = currentContent.getPlainText('').length;
        let textToInsert = text.replace(/\n/g, ' ');

        if (maxLength) {
            const availableSpace = maxLength - currentLength;
            if (availableSpace <= 0) return 'handled';
            textToInsert = textToInsert.slice(0, availableSpace);
        }

        const newEditorState = EditorState.push(
            this.state.editorState,
            Modifier.replaceText(
                currentContent,
                this.state.editorState.getSelection(),
                textToInsert
            ), 'insert-characters'
        );

        this.onChange(newEditorState);
        return 'handled';
    }

    private handleBeforeInput(chars: string, editorState: EditorState): DraftHandleValue {
        if (!this.props.maxLength) {
            return 'not-handled';
        }

        const currentContent = editorState.getCurrentContent();
        const currentLength = currentContent.getPlainText('').length;

        if (currentLength >= this.props.maxLength -1) {
            return 'handled';
        }

        return 'not-handled';
    }

    public render(): ReactNode {
        const { MentionSuggestions } = this.mentionPlugin;
        const { EmojiSuggestions } = this.emojiPlugin;

        return (
            <div className="InteractiveTextInputs">
                <Editor
                    onBlur={this.props.onBlur}
                    handlePastedText={this.handlePaste}
                    handleBeforeInput={this.handleBeforeInput}
                    ariaLabel={localize("SkrivEnKommentar")}
                    ref={this.editorRef}
                    editorState={this.state.editorState}
                    onChange={this.onChange}
                    onFocus={this.props.onFocus}
                    keyBindingFn={this.myKeyBindingFn.bind(this)}
                    handleKeyCommand={this.handleKeyCommand.bind(this)}
                    placeholder={this.props.placeholder ?
                        this.props.placeholder :
                        localize("COMPOSER_PLACEHOLDER")}
                    plugins={[
                        this.mentionPlugin,
                        this.emojiPlugin
                    ]}
                    readOnly={this.props.readOnly}
                />
                <MentionSuggestions
                    open={this.state.mentionOpen}
                    onOpenChange={this.onMentionOpenChanged}
                    onAddMention={this.onAddMention}
                    suggestions={this.state.suggestions}
                    onSearchChange={this.onSearchChange}
                />
                <EmojiSuggestions />
            </div>
        );
    }

    protected onMentionOpenChanged = (open: boolean): void => {
        this.setState({ mentionOpen: open });
    }

    protected onAddMention(_: MentionData) {
        console.log(_)
        /* ignore */
    }

    protected onChange(editorState: EditorState) {
        this.setState(
            { editorState }, 
            () => {
                if (this.props.onChange) {
                    this.props.onChange();
                }
            },
        );
    }

    protected onSearchChange(event: {
        trigger: string;
        value: string;
    }): void {
        const { value } = event;

        if (value) {
            this.mentionSubject.next(value);
            return;
        }

        this.setState(
            { suggestions: [] },
            () => this.mentionSubject.next(value)
        );
    }
}

export default InteractiveTextInput;